import { useMemo, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import type { PurchaseOrder } from '@/lib/api/types';
import { EmptyState, Page, PageBody, PageHeader, Panel, Stat, StatRow } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Badge } from '@/ui/Badge';
import { Field, Input, MoneyInput, QuantityInput, Textarea } from '@/ui/Field';
import { countLabel, money, qty } from '@/lib/format';
import { checkQuantity, quantityForRequest } from '@/lib/quantity';
import { poKeys } from '@/features/buying/api';
import { isoFromExpectedOn } from '@/features/buying/dates';
import { localToday } from '@/features/buying/CountConfirmation';
import { newRequestKey } from '@/features/buying/request-key';
import { LeaveGuardDialog, useLeaveGuard } from '@/features/buying/leave-guard';
import { ProductPicker, VendorSelect, useVendorOptions } from '@/features/buying/parts';
import type { PickedProduct } from '@/features/buying/parts';

export const Route = createFileRoute('/_app/purchasing/new')({
  staticData: { crumb: 'New PO' },
  validateSearch: (s: Record<string, unknown>): { vendorId?: number } => ({
    vendorId: Number(s.vendorId) > 0 ? Number(s.vendorId) : undefined,
  }),
  component: NewPurchaseOrder,
});

type CartLine = {
  key: string;
  productId: number;
  productName: string;
  /** Raw text of the quantity box; read once more when the request is built. */
  orderedQtyText: string;
  /**
   * TASK-0241: the estimated cost per unit (catalog or last cost, or typed over). Sent as
   * `orderedUnitCost`, never `unitCost`: an estimate never becomes product cost; the invoice sets it.
   */
  unitCostCents: number | null;
  /**
   * `POST /vendors/purchase-orders` 422s when the line unit disagrees with the
   * product's canonical unit, so this is taken from the product, never chosen.
   */
  unit: 'ea' | 'lb';
  vendorSku: string | null;
  linkedToVendor: boolean;
};

function NewPurchaseOrder() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vendors = useVendorOptions();

  const [vendorId, setVendorId] = useState<number | null>(search.vendorId ?? null);
  const [expectedAt, setExpectedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<CartLine[]>([]);
  // TASK-0241: one key per screen, so a retried create (a lost answer) returns the same draft.
  const requestKey = useRef<string>(newRequestKey());
  // Typed lines are saved nowhere until the draft is created: ask before leaving.
  const guard = useLeaveGuard(lines.length > 0);

  const checks = useMemo(
    () => lines.map((line) => checkQuantity(line.orderedQtyText, line.unit)),
    [lines]
  );
  /** Null while the line's box does not read: shown as a dash, adds nothing. */
  const lineTotal = (index: number): number | null => {
    const quantity = checks[index]?.quantity ?? null;
    return quantity === null
      ? null
      : Math.round(quantity * (lines[index]?.unitCostCents ?? 0));
  };
  const total = lines.reduce((sum, _, i) => sum + (lineTotal(i) ?? 0), 0);

  const lineProblems = lines.map((line, i) => {
    const check = checks[i]!;
    if (check.problem !== null) return `${line.productName}: quantity ${check.problem}.`;
    if (check.quantity <= 0) return `${line.productName}: quantity must be above zero.`;
    return null;
  });
  const problems = lineProblems.filter((problem) => problem !== null);
  const uncosted = lines.filter((line) => line.unitCostCents === null);
  // TASK-0241: a past expected day warns, it does not block.
  const expectedDay = isoFromExpectedOn(expectedAt);
  const pastExpected = expectedDay !== null && expectedDay < localToday();
  const offCatalog = lines.filter((line) => !line.linkedToVendor);

  const create = useMutation({
    mutationFn: () =>
      api.post<PurchaseOrder>('/vendors/purchase-orders', {
        vendorId,
        clientRequestId: requestKey.current,
        // The plain date, as typed: the server stores it as that UTC day.
        ...(isoFromExpectedOn(expectedAt)
          ? { expectedAt: isoFromExpectedOn(expectedAt) }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        // The one conversion from typed text; throws rather than send a
        // blank or unreadable box.
        items: lines.map((line) => {
          const orderedQty = quantityForRequest(
            line.orderedQtyText,
            line.unit,
            line.productName
          );
          if (orderedQty <= 0) {
            throw new Error(`${line.productName}: quantity must be above zero.`);
          }
          return {
            productId: line.productId,
            orderedQty,
            // An estimate, never a cost (TASK-0241).
            orderedUnitCost: line.unitCostCents ?? null,
            unit: line.unit,
          };
        }),
      }),
    onSuccess: (po) => {
      // Saved: the redirect below is the screen's own, not a way out of unsaved work.
      guard.allowLeave();
      void queryClient.invalidateQueries({ queryKey: poKeys.all });
      queryClient.setQueryData(poKeys.detail(po.id), po);
      toast.success(
        `PO #${po.id} created as a draft — ${countLabel(lines.length, 'line')}.`
      );
      void navigate({
        to: '/purchasing/$id',
        params: { id: String(po.id) },
        search: { mode: 'lines' as const },
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const addLine = (product: PickedProduct) =>
    setLines((prev) => [
      ...prev,
      {
        key: `${product.id}-${prev.length}`,
        productId: product.id,
        productName: product.name,
        orderedQtyText: '1',
        unitCostCents: product.vendorUnitCost ?? product.cost ?? null,
        unit: product.unit,
        vendorSku: product.vendorSku,
        linkedToVendor: product.linkedToVendor,
      },
    ]);

  const ready = vendorId !== null && lines.length > 0 && problems.length === 0;

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/purchasing" search={{}} className="hover:text-ink">
            Purchasing
          </Link>
        }
        title="New purchase order"
        subtitle="Builds a draft. Nothing is ordered or received until you say so."
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              asChild
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link to="/purchasing" search={{}}>
                Cancel
              </Link>
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!ready}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create draft PO
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <Panel title="Vendor">
              <Field
                label="Supplier"
                required
                hint="Search finds an exact barcode or code first, then names. Items this vendor sells are tagged linked, with their catalog cost."
              >
                <VendorSelect
                  value={vendorId}
                  vendors={vendors.data}
                  loading={vendors.isPending}
                  onChange={(next) => setVendorId(next)}
                />
              </Field>
            </Panel>

            <Panel
              title={`Lines · ${countLabel(lines.length, 'line')}`}
              allowOverflow
              description={
                vendorId === null
                  ? 'Choose a vendor first so costs prefill from their catalog.'
                  : undefined
              }
              bleed
              footer={
                <div className="flex items-center justify-between">
                  <span>Order total</span>
                  <span className="text-ink font-mono text-sm tabular-nums">
                    {money(total)}
                  </span>
                </div>
              }
            >
              <div className="border-line border-b p-3">
                <ProductPicker
                  vendorId={vendorId}
                  disabledIds={new Set(lines.map((line) => line.productId))}
                  onPick={addLine}
                  placeholder={
                    vendorId === null
                      ? 'Pick a vendor first…'
                      : 'Search products to add to this order…'
                  }
                />
              </div>

              {lines.length === 0 ? (
                <EmptyState
                  compact
                  icon={<ShoppingCart />}
                  title="No lines yet"
                  description="Search above to add products. Costs prefill from the vendor's catalog where a link exists."
                />
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
                      <th className="px-3 py-1.5 text-left font-medium">Product</th>
                      <th className="w-24 px-2 py-1.5 text-right font-medium">Qty</th>
                      <th
                        className="w-28 px-2 py-1.5 text-right font-medium"
                        title="What we expect to pay per unit. The invoice sets the real cost."
                      >
                        Est. cost
                      </th>
                      <th className="w-24 px-2 py-1.5 text-right font-medium">Line</th>
                      <th className="w-9" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr
                        key={line.key}
                        className="border-line-soft border-b last:border-0"
                      >
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-ink truncate">{line.productName}</span>
                            {line.unit === 'lb' ? <Badge tone="info">lb</Badge> : null}
                            {line.linkedToVendor ? null : (
                              <Badge tone="warn">not in vendor catalog</Badge>
                            )}
                          </div>
                          {line.vendorSku ? (
                            <div className="text-2xs text-ink-faint mt-0.5 font-mono">
                              {line.vendorSku}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5">
                          <QuantityInput
                            size="sm"
                            className="w-full"
                            unit={line.unit}
                            selectOnFocus
                            aria-label={`Quantity for ${line.productName}`}
                            value={line.orderedQtyText}
                            invalid={lineProblems[index] != null}
                            onValueChange={(text) =>
                              setLines((prev) =>
                                prev.map((l, i) =>
                                  i === index ? { ...l, orderedQtyText: text } : l
                                )
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <MoneyInput
                            size="sm"
                            value={line.unitCostCents}
                            aria-label={`Est. cost for ${line.productName}`}
                            onValueChange={(cents) =>
                              setLines((prev) =>
                                prev.map((l, i) =>
                                  i === index ? { ...l, unitCostCents: cents } : l
                                )
                              )
                            }
                          />
                        </td>
                        <td className="text-ink px-2 py-1.5 text-right font-mono tabular-nums">
                          {lineTotal(index) === null ? '—' : money(lineTotal(index))}
                        </td>
                        <td className="px-1 py-1.5">
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Remove ${line.productName}`}
                            onClick={() =>
                              setLines((prev) => prev.filter((_, i) => i !== index))
                            }
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          <div className="space-y-4">
            <StatRow className="grid-cols-2 md:grid-cols-2 xl:grid-cols-2">
              <Stat label="Lines" value={qty(lines.length)} />
              <Stat label="Order total" value={money(total)} />
            </StatRow>

            <Panel title="Delivery">
              <div className="space-y-3">
                <Field
                  label="Expected"
                  hint={
                    pastExpected
                      ? 'This day is in the past. The order still saves.'
                      : 'Optional. Shown on the purchasing list.'
                  }
                >
                  <Input
                    type="date"
                    size="sm"
                    mono
                    value={expectedAt}
                    invalid={pastExpected}
                    onChange={(e) => setExpectedAt(e.currentTarget.value)}
                  />
                </Field>
                <Field label="Notes">
                  <Textarea
                    rows={5}
                    value={notes}
                    placeholder="Standing order, delivery window, anything the receiver needs…"
                    onChange={(e) => setNotes(e.currentTarget.value)}
                  />
                </Field>
              </div>
            </Panel>

            {(uncosted.length > 0 ||
              offCatalog.length > 0 ||
              problems.length > 0 ||
              pastExpected) &&
            lines.length > 0 ? (
              <Panel title="Before you save">
                <ul className="text-2xs text-ink-muted space-y-1.5">
                  {problems.map((problem, index) => (
                    <li key={index} className="text-negative">
                      {problem}
                    </li>
                  ))}
                  {uncosted.length > 0 ? (
                    <li>
                      {countLabel(uncosted.length, 'line')} have no estimated cost. The PO
                      saves, but those lines add nothing to the estimated total.
                    </li>
                  ) : null}
                  {pastExpected ? (
                    <li className="text-warn">The expected day is in the past.</li>
                  ) : null}
                  {offCatalog.length > 0 ? (
                    <li>
                      {countLabel(offCatalog.length, 'line')} are not linked to this
                      vendor. That is allowed — link them on the vendor page to get cost
                      prefill next time.
                    </li>
                  ) : null}
                </ul>
              </Panel>
            ) : null}
          </div>
        </div>
      </PageBody>
      <LeaveGuardDialog
        guard={guard}
        title="Leave without creating the order?"
        description={`Nothing on this order is saved yet: ${countLabel(lines.length, 'line')} would be lost. Create the draft first.`}
      />
    </Page>
  );
}
